import React from 'react';
import {
  Box,
  Button,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import type { SxProps } from '@mui/system';

import LockIcon from '@mui/icons-material/Lock';

import WidgetHeading from './WidgetHeading';
import { useDashboardContext } from 'screens/dashboard/Dashboard';

type TWidgetMaskProps = {
  isDashboard?: boolean;
  isKpi?: boolean;
  title?: string; // The widget title.
  button?: {
    text: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  };
};

const linkToDataInsightsPro = () =>
  window.open(
    'https://www.bloxdigital.com/upgrade/advanced-analytics/',
    '_blank',
    'noopener,noreferrer',
  );

const UpgradeAnalyticsButton = ({
  button,
  sx,
}: {
  button: TWidgetMaskProps['button'];
  sx?: SxProps;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      data-testid="widget-mask-upgrade-analytics-button"
      variant="contained"
      color="primary"
      sx={{ textAlign: 'center', ...sx }}
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        if (!!button?.onClick) {
          button.onClick(event);
        } else {
          linkToDataInsightsPro();
        }
      }}
    >
      {button?.text || t('blox.general.contact_sales')}
    </Button>
  );
};

/**
 * Mask either a KPI or a Widget with a blur effect and a title--fake data is handled in WidgetCard.tsx.
 * @param param0 Props for the WidgetMask component.
 * @returns JSX.Element
 * @description This component displays a mask over a widget or KPI, providing a title and a button to contact sales.
 */
const WidgetMask = ({ isDashboard, isKpi, title, button }: TWidgetMaskProps) => {
  const { t } = useTranslation();
  const { isDashboardMasked } = useDashboardContext();
  const theme = useTheme();
  const isSmallViewport = useMediaQuery(theme.breakpoints.down('sm'));

  const heading = isDashboard
    ? t('blox.dashboards.mask.dashboard')
    : t('blox.dashboards.mask.title', {
        title: title || t('blox.dashboards.exports.label'),
      });

  const isWidgetInMaskedDashboard = isDashboardMasked && !isDashboard;
  const unlockTitle = t('blox.dashboards.mask.title', {
    title,
  });

  // KPI Mask.
  if (isKpi) {
    return (
      <Box
        className="kpi-mask"
        data-testid="kpi-mask"
        sx={{
          height: '100%',
          width: '100%',
          h6: {
            pb: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <WidgetHeading
            sx={{
              fontSize: '1rem',
              py: 0,
              pb: 0,
            }}
          >
            {title}
          </WidgetHeading>

          <Tooltip
            title={t('blox.dashboards.mask.tooltip')}
            placement="top"
          >
            <LockIcon
              fontSize="small"
              sx={{ color: 'text.secondary', fontSize: '1rem', ml: 1 }}
            />
          </Tooltip>
        </Box>

        {!isSmallViewport && (
          <Box
            data-testid="kpi-mask-cta"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'end',
            }}
          >
            <Tooltip
              title={unlockTitle}
              placement="top"
            >
              <Box>
                <WidgetHeading>
                  {unlockTitle.length > 20 ? `${unlockTitle.slice(0, 20)}…` : unlockTitle}
                </WidgetHeading>
              </Box>
            </Tooltip>

            <Box>
              <UpgradeAnalyticsButton button={button} />
            </Box>
          </Box>
        )}

        {isSmallViewport && (
          <UpgradeAnalyticsButton
            button={button}
            sx={{
              width: '100%',
              mt: 2,
            }}
          />
        )}
      </Box>
    );
  }

  // Widget Mask.
  return (
    <Box
      className="widget-mask"
      data-testid="widget-mask"
      sx={{
        height: '100%',
        width: '100%',
        backdropFilter: 'blur(4px)',
        position: 'absolute',
        zIndex: isWidgetInMaskedDashboard ? 0 : 999,
        top: 0,
        left: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: isDashboard ? 'flex-start' : 'center',
      }}
    >
      <Paper
        data-testid="widget-mask-content"
        sx={{
          p: 0,
          borderRadius: 1,
          mt: isDashboard ? 4 : 0,
          maxWidth: 500,
          display: isWidgetInMaskedDashboard ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme?.blox?.colors?.offwhite
              : theme?.blox?.colors?.offblack,
        }}
      >
        <Box
          data-testid="widget-mask-heading"
          sx={(theme) => {
            const isLightTheme = theme.palette.mode === 'light';
            const backgroundColor = isLightTheme ? grey[100] : grey[900];
            const borderColor = isLightTheme
              ? theme?.blox?.colors?.lightgray
              : theme?.blox?.colors?.darkgray;
            return {
              width: '100%',
              px: 2,
              pt: 2,
              borderRadius: '4px 4px 0 0',
              borderBottom: `1px solid ${borderColor}`,
              backgroundColor,
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
            };
          }}
        >
          <WidgetHeading
            sx={{
              fontSize: '1.25rem',
              pt: 0,
            }}
          >
            {heading}
          </WidgetHeading>
          <LockIcon
            sx={{
              fontSize: '1.25rem',
              color: (theme) => theme.palette.text.secondary,
            }}
          />
        </Box>

        <Box
          data-testid="widget-mask-description"
          sx={{
            p: 2,
          }}
        >
          <Typography
            sx={{
              textAlign: 'center',
              mt: 1,
              mb: 1,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            {t('blox.dashboards.mask.description')}
          </Typography>
        </Box>

        <Box
          data-testid="widget-mask-footer"
          sx={(theme) => {
            const isLightTheme = theme.palette.mode === 'light';
            const backgroundColor = isLightTheme ? grey[100] : grey[900];
            const borderColor = isLightTheme
              ? theme?.blox?.colors?.lightgray
              : theme?.blox?.colors?.darkgray;
            return {
              p: 2,
              width: '100%',
              backgroundColor,
              borderColor,
              borderTop: `1px solid ${borderColor}`,
              borderRadius: '0 0 4px 4px',
              textAlign: 'center',
            };
          }}
        >
          <UpgradeAnalyticsButton button={button} />
        </Box>
      </Paper>
    </Box>
  );
};

export default WidgetMask;
